import { ChannelServerData } from './channel';
import { Like, LikeBySpeakerServerData, LikeByUserServerData } from './like';
import {
  ParticipantServerData,
  SpeakerServerData,
  TeamType,
} from './participant';
import { User } from './user';

export const enum ShowType {
  Common = 'common',
  Sideless = 'sideless',
}

export const enum ShowStatus {
  Live = 'live',
  Finished = 'finished',
  Scheduled = 'scheduled',
}

export const enum ScheduledShowStatus {
  Started = 'started',
  Pending = 'pending',
  Expired = 'expired',
  Canceled = 'canceled',
}

export const enum ChatMessageType {
  Text = 'text',
  Gif = 'gif',
}

export type ShowCreatorFirstStep = {
  title: string;
  image: string | null;
  blueSide?: string;
  redSide?: string;
  coHost?: string | null;
  coHostSide?: TeamType;
  hostSide?: TeamType;
  type?: ShowType;
};

export type ShowCreatorSecondStep = {
  minBid: number;
  priceForListen?: number | null;
  recorded: boolean;
  scheduled: boolean;
  scheduledDate?: string;
  channel?: string;
};

export type ShowCreateData = ShowCreatorFirstStep & ShowCreatorSecondStep;

export type Show = {
  id: string;
  status: ShowStatus;
  type: ShowType;
  hostId: string;
  coHostId?: string;
  listenersCount: number;
  anonymousListenersCount?: number;
  rightTeamSpeakerId: null | string;
  leftTeamSpeakerId: null | string;
  createdDate: null | string;
  updatedDate: null | string;
  currentTopicId?: string;
  minBid: number;
  priceForListen?: number;
  recorded: boolean;
  channelId?: string;
  isFreeShow?: boolean;
  sid?: string;
  isRated?: boolean;
  recordDuration?: string;
  scheduledShow?: null | ScheduledShow;
  statistics?: HostStatistics & {
    topUsers?: { userId: string; likesCount: number }[];
    debates?: string[];
    numberOfSpeakers?: number;
  };
} & ShowCreatorFirstStep;

export type ScheduledShow = {
  id: string;
  startAt: string;
  status: ScheduledShowStatus;
  isSubscribed?: boolean;
};

export type HostStatistics = {
  percentageComparedToLastShow?: number;
  totalLikes?: number;
  guestRequests?: number;
  earnings?: number;
};

export type ParticipantStatistics = {
  topUsers?: { user: User; likesCount: number }[];
  numberOfSpeakers?: number;
};

export type Topic = {
  id: string;
  leftVotes?: number;
  rightVotes?: number;
  myVoteSide?: TeamType | null;
  redSide?: string;
  blueSide?: string;
  title?: string;
};

export type TopicServerData = {
  id: string;
  title: string;
  image: string | null;
  blueSide: string;
  redSide: string;
  likes: Like[];
  show: { id: string };
  speakers?: SpeakerServerData[];
  createdAt: Date;
  endedAt: Date;
};

export type ShowServerData = {
  id: string;
  status: ShowStatus;
  type: ShowType;
  host: User;
  coHost: User;
  participants: ParticipantServerData[];
  listenersCount?: number;
  speakers?: SpeakerServerData[];
  createdDate: null | string;
  updatedDate: null | string;
  topics?: TopicServerData[];
  currentTopicId?: string;
  minBid: number;
  priceForListen: number;
  recorded: boolean;
  channel?: ChannelServerData;
  isFreeShow?: boolean;
  needToPay?: boolean;
  sid?: string;
  recordDuration?: string;
  scheduledShow?: null | ScheduledShow;
  likes: {
    byUser: LikeByUserServerData[];
    bySpeaker: LikeBySpeakerServerData[];
  };
} & ShowCreatorFirstStep;

export type RecordedShowServerData = ShowServerData & {
  topics: TopicServerData;
  speakers: SpeakerServerData;
  participants: ParticipantServerData;
  isRated?: boolean;
};

export type ChatMessageServerData = {
  id: string;
  type: ChatMessageType;
  content: string;
  pinned?: boolean;
  createdAt?: Date;
  user: { id: string; name: string; settings?: { usernameColor?: string } };
  show: { id: string };
  pinnedBy?: string;
};

export type ChatMessage = {
  id: string;
  type: ChatMessageType;
  content: string;
  pinned?: boolean;
  createdAt?: Date;
  userId: string;
  showId: string;
};
